/**
 * Responsive Page Implementation Helpers
 * Quick utilities to apply responsive design system to React pages
 */

import { responsive, componentStyles, typography, layouts } from './responsiveDesign';

/**
 * Component style combinations for common patterns
 */
export const pageStyles = {
  // Container wrappers
  mainContainer: `${layouts.pageContainer}`,
  contentWrapper: `${layouts.pageContent}`,
  
  // Common grid patterns
  responsiveGrid2Col: `grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8`,
  responsiveGrid3Col: `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8`,
  responsiveGrid4Col: `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6`,
  
  // Stats/metric cards
  metricCard: `${componentStyles.card} p-4 md:p-6`,
  metricValue: `text-3xl md:text-4xl font-bold text-teal-600`,
  metricLabel: `text-xs md:text-sm font-semibold text-gray-600 uppercase tracking-wide`,
  
  // Button groups
  buttonGroup: `flex flex-wrap gap-2 md:gap-3 mb-6 md:mb-8`,
  buttonGroupLarge: `flex flex-wrap gap-3 md:gap-4 mb-8 md:mb-12`,
  
  // Table/list containers
  tableContainer: `overflow-x-auto rounded-lg border border-gray-200`,
  tableWrapper: `inline-block min-w-full`,
  
  // Section headers
  sectionHeader: `${typography.h2} mb-4 md:mb-6 text-gray-900`,
  subsectionHeader: `${typography.h3} mb-3 md:mb-4 text-gray-800`,
  
  // Responsive padding
  sectionPadding: `px-4 md:px-6 py-4 md:py-6`,
  cardPadding: `p-4 md:p-6`,
  
  // Common spacing
  spacingXs: `mb-2 md:mb-3`,
  spacingSm: `mb-3 md:mb-4`,
  spacingMd: `mb-4 md:mb-6`,
  spacingLg: `mb-6 md:mb-8`,
  spacingXl: `mb-8 md:mb-12`,
  
  // Responsive text
  textResponsive: `text-xs md:text-sm lg:text-base`,
  textResponsiveLarge: `text-base md:text-lg lg:text-xl`,
};

/**
 * Get responsive button class based on variant
 */
export const getResponsiveButton = (variant: 'primary' | 'secondary' | 'danger' = 'primary') => {
  const baseClass = `text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 transition-all duration-200`;
  
  switch (variant) {
    case 'primary':
      return `${componentStyles.buttonPrimary} ${baseClass}`;
    case 'secondary':
      return `${componentStyles.buttonSecondary} ${baseClass}`;
    case 'danger':
      return `${componentStyles.buttonDanger} ${baseClass}`;
    default:
      return `${componentStyles.buttonPrimary} ${baseClass}`;
  }
};

/**
 * Build responsive grid class based on column count and breakpoints
 */
export const buildResponsiveGrid = (
  cols: 1 | 2 | 3 | 4 | 6,
  breakpoint: 'sm' | 'md' | 'lg' = 'md',
  gap: 'sm' | 'md' | 'lg' = 'md'
) => {
  const baseClass = 'grid grid-cols-1';
  const gapClass = gap === 'sm' ? 'gap-3' : gap === 'lg' ? 'gap-6 md:gap-8' : 'gap-4 md:gap-6';
  
  let breakpointClass = '';
  switch (breakpoint) {
    case 'sm':
      breakpointClass = cols === 2 ? 'sm:grid-cols-2' : cols === 3 ? 'sm:grid-cols-2 md:grid-cols-3' : '';
      break;
    case 'md':
      breakpointClass = cols === 2 ? 'md:grid-cols-2' : cols === 3 ? 'md:grid-cols-3' : cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : '';
      break;
    case 'lg':
      breakpointClass = cols === 2 ? 'lg:grid-cols-2' : cols === 3 ? 'lg:grid-cols-3' : cols === 4 ? 'lg:grid-cols-4' : '';
      break;
  }
  
  return `${baseClass} ${breakpointClass} ${gapClass}`;
};

/**
 * Action button row styling wrapper
 */
export const actionButtonRow = `flex flex-wrap gap-2 md:gap-3 mb-6 md:mb-8`;

/**
 * Common page layout patterns
 */
export const pageLayouts = {
  // Standard admin page
  adminPage: `${layouts.pageContainer} ${layouts.pageContent}`,
  
  // Sidebar + content
  sidebarLayout: `grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8`,
  sidebarContent: `lg:col-span-3`,
  sidebar: `lg:col-span-1`,
  
  // Full-width sections
  section: `${pageStyles.spacingLg}`,
  sectionNarrow: `max-w-4xl mx-auto ${pageStyles.spacingLg}`,
};

export default pageStyles;
